/**
 * Phase 7.1 — sidebar information architecture.
 * Routes unchanged; only nav grouping and labels.
 */

import {
  SHOW_COMPETITORS_UI,
  SHOW_GROUP_SHARING,
  SHOW_INSTAGRAM_UI,
} from './featureFlags';

export const NAV_DRAWER_WIDTH = 220;
export const NAV_RAIL_COLLAPSED_WIDTH = 72;
export const NAV_FLYOUT_WIDTH = 240;

const SCHEDULING_PATHS = ['/scheduling', '/schedule', '/scheduled-posts'];

const FLAG_MAP = {
  SHOW_GROUP_SHARING,
  SHOW_INSTAGRAM_UI,
  SHOW_COMPETITORS_UI,
};

function clientsMatch(pathname) {
  if (pathname === '/clients') return true;
  if (pathname.startsWith('/clients/') && !pathname.includes('oauth-callback')) {
    return true;
  }
  return false;
}

function mlOverviewMatch(loc) {
  if (loc.pathname !== '/ml-insights') return false;
  return new URLSearchParams(loc.search).get('tab') !== 'rankings';
}

function mlRankingsMatch(loc) {
  if (loc.pathname !== '/ml-insights') return false;
  return new URLSearchParams(loc.search).get('tab') === 'rankings';
}

function reportsSectionMatch(loc) {
  return loc.pathname === '/reports' || loc.pathname === '/reports/model';
}

/** @typedef {{ label: string, path: string, description?: string, match: (loc: { pathname: string, search: string }) => boolean, flag?: string }} NavLink */
/** @typedef {{ type: 'section', label: string, match: (loc: { pathname: string, search: string }) => boolean, children: NavLink[] }} NavSection */
/** @typedef {{ id: string, label: string, icon: string, description?: string, children?: (NavLink|NavSection)[], directPath?: string, match?: (loc: { pathname: string, search: string }) => boolean }} NavRailItem */

/** @type {NavRailItem[]} */
export const NAV_RAIL = [
  {
    id: 'workspace',
    label: 'Workspace',
    icon: 'Dashboard',
    description: 'Dashboard and client workspaces',
    children: [
      {
        label: 'Dashboard',
        path: '/',
        description: 'Overview and activation',
        match: (loc) => loc.pathname === '/',
      },
      {
        label: 'Clients',
        path: '/clients',
        description: 'Manage clients and platform connections',
        match: (loc) => clientsMatch(loc.pathname),
      },
    ],
  },
  {
    id: 'publishing',
    label: 'Publishing',
    icon: 'PostAdd',
    description: 'Create, schedule, and distribute content',
    children: [
      {
        label: 'Posts',
        path: '/posts',
        description: 'Create and manage posts',
        match: (loc) => loc.pathname === '/posts',
      },
      {
        label: 'Scheduling',
        path: '/scheduling',
        description: 'Calendar and publish queue',
        match: (loc) => SCHEDULING_PATHS.includes(loc.pathname),
      },
      {
        label: 'Gallery',
        path: '/gallery',
        description: 'Media library',
        match: (loc) => loc.pathname === '/gallery',
      },
      {
        label: 'Campaigns',
        path: '/campaigns',
        description: 'Campaign management',
        match: (loc) => loc.pathname === '/campaigns',
      },
      {
        label: 'Image Directories',
        path: '/image-directories',
        description: '500px and DeviantArt posting',
        match: (loc) => loc.pathname === '/image-directories',
      },
      {
        label: 'Group Sharing',
        path: '/group-sharing',
        description: 'Share Page posts to Facebook groups',
        flag: 'SHOW_GROUP_SHARING',
        match: (loc) => loc.pathname === '/group-sharing',
      },
      {
        label: 'Instagram Automation',
        path: '/instagram-automation',
        description: 'Instagram automation tools',
        flag: 'SHOW_INSTAGRAM_UI',
        match: (loc) => loc.pathname === '/instagram-automation',
      },
    ],
  },
  {
    id: 'intelligence',
    label: 'Intelligence',
    icon: 'Insights',
    description: 'Analytics, ML, and competitors',
    children: [
      {
        label: 'Analytics',
        path: '/analytics',
        description: 'Performance analytics',
        match: (loc) => loc.pathname === '/analytics',
      },
      {
        label: 'ML Insights',
        path: '/ml-insights',
        description: 'ML analysis and competitor data',
        match: mlOverviewMatch,
      },
      {
        label: 'Competitors',
        path: '/competitors',
        description: 'Competitor tracking',
        flag: 'SHOW_COMPETITORS_UI',
        match: (loc) => loc.pathname === '/competitors',
      },
      {
        label: 'Rankings',
        path: '/ml-insights?tab=rankings',
        description: 'Keyword ranking tracking',
        match: mlRankingsMatch,
      },
    ],
  },
  {
    // Top-level on purpose, not a child of Intelligence. Review sends are a
    // different unit of measurement from post performance — no model, no
    // engagement rate, nothing scored — and nesting them under Analytics or
    // Competitors would import framing that does not apply.
    id: 'reputation',
    label: 'Reputation',
    icon: 'RateReview',
    description: 'Review request send log',
    directPath: '/reputation',
    match: (loc) => loc.pathname === '/reputation',
  },
  {
    id: 'ai-content',
    label: 'AI Content',
    icon: 'AutoAwesome',
    description: 'AI-powered content generation',
    directPath: '/ai-content',
    match: (loc) => loc.pathname === '/ai-content',
  },
  {
    id: 'operations',
    label: 'Operations',
    icon: 'CloudQueue',
    description: 'Monitoring, reports, and docs',
    children: [
      {
        label: 'Task Monitoring',
        path: '/monitoring',
        description: 'Celery workers and background tasks',
        match: (loc) => loc.pathname === '/monitoring',
      },
      {
        type: 'section',
        label: 'Reports',
        match: reportsSectionMatch,
        children: [
          {
            label: 'Competitive Reports',
            path: '/reports',
            description: 'Export competitive intelligence',
            match: (loc) => loc.pathname === '/reports',
          },
          {
            label: 'Model Report',
            path: '/reports/model',
            description: 'Prediction accuracy and calibration',
            match: (loc) => loc.pathname === '/reports/model',
          },
        ],
      },
      {
        label: 'Docs',
        path: '/docs',
        description: 'Documentation and guides',
        match: (loc) =>
          loc.pathname === '/docs' || loc.pathname.startsWith('/docs/'),
      },
      {
        label: 'Campaigns guide',
        path: '/docs/campaigns',
        description: 'What campaigns are and how to create them',
        match: (loc) => loc.pathname === '/docs/campaigns',
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    icon: 'Settings',
    description: 'Account preferences',
    directPath: '/settings',
    match: (loc) => loc.pathname === '/settings',
  },
];

function isSection(entry) {
  return entry && entry.type === 'section';
}

function flagVisible(entry) {
  if (!entry.flag) return true;
  return Boolean(FLAG_MAP[entry.flag]);
}

function filterChildren(children) {
  if (!children) return [];
  return children
    .filter(flagVisible)
    .map((entry) => {
      if (isSection(entry)) {
        const nested = entry.children.filter(flagVisible);
        if (nested.length === 0) return null;
        return { ...entry, children: nested };
      }
      return entry;
    })
    .filter(Boolean);
}

/** Nav rail with feature flags applied. */
export function getNavRail() {
  return NAV_RAIL.map((item) => {
    if (item.directPath) return item;
    const children = filterChildren(item.children);
    return { ...item, children };
  }).filter((item) => {
    if (item.directPath) return true;
    return item.children && item.children.length > 0;
  });
}

function flattenLinks(children, groupLabel, acc) {
  if (!children) return acc;
  for (const entry of children) {
    if (isSection(entry)) {
      for (const link of entry.children) {
        acc.push({
          ...link,
          groupLabel,
          sectionLabel: entry.label,
        });
      }
    } else if (entry.path) {
      acc.push({ ...entry, groupLabel });
    }
  }
  return acc;
}

export function flattenNavForSearch() {
  const acc = [];
  for (const item of getNavRail()) {
    if (item.directPath) {
      acc.push({
        label: item.label,
        path: item.directPath,
        description: item.description,
        groupLabel: item.label,
      });
    } else {
      flattenLinks(item.children, item.label, acc);
    }
  }
  return acc;
}

function linkMatches(loc, link) {
  return link.match(loc);
}

function sectionOrLinkActive(loc, entry) {
  if (isSection(entry)) {
    return entry.match(loc) || entry.children.some((c) => linkMatches(loc, c));
  }
  return linkMatches(loc, entry);
}

export function isNavGroupActive(item, loc) {
  if (item.directPath) {
    return item.match ? item.match(loc) : loc.pathname === item.directPath;
  }
  return item.children?.some((c) => sectionOrLinkActive(loc, c)) ?? false;
}

export function getActiveNavTitle(loc) {
  for (const item of getNavRail()) {
    if (item.directPath && isNavGroupActive(item, loc)) {
      return item.label;
    }
    if (!item.children) continue;
    for (const entry of item.children) {
      if (isSection(entry)) {
        for (const link of entry.children) {
          if (linkMatches(loc, link)) return link.label;
        }
      } else if (linkMatches(loc, entry)) {
        return entry.label;
      }
    }
    if (isNavGroupActive(item, loc)) {
      return item.label;
    }
  }
  return 'Dashboard';
}

export function parseNavPath(path) {
  const [pathname, search = ''] = path.split('?');
  return { pathname, search: search ? `?${search}` : '' };
}
